import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Minimum role required. If omitted, any authenticated user is allowed. */
  role?: string
}

/**
 * Wraps a route so that unauthenticated users are redirected to /login.
 * If a `role` is specified, users without that role get a 403 screen instead.
 * Preserves the attempted URL in location state so LoginPage can redirect back.
 */
export default function ProtectedRoute({ children, role }: ProtectedRouteProps) {
  const { user, isAuthenticated, initialising } = useAuth()
  const location = useLocation()

  if (initialising) {
    // Auth state is still being restored from the cookie — render nothing yet
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (role && user?.role !== role) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You need the <strong>{role}</strong> role to view this page.</p>
      </div>
    )
  }

  return <>{children}</>
}
